import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { toast } from "sonner";
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  Eye,
  Inbox,
  ListChecks,
  Loader2,
  LogOut,
  Menu,
  RefreshCw,
  ScrollText,
  Settings as SettingsIcon,
  ShieldCheck,
  Upload,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { errMessage, gmailStatus, gmailSync } from "@/lib/api";
import { initials, relTime } from "@/lib/format";
import { useAuth } from "@/auth/AuthProvider";
import { BrandIcon, BrandStripe, BrandWordmark } from "@/components/BrandLogo";
import { ThemeToggle } from "@/theme/ThemeToggle";

// Top bar stays lean: the four working screens only.
// Activity + Settings are admin-only utilities and live in the avatar menu.
const NAV = [
  { to: "/", label: "Inbox", icon: Inbox, testId: "nav-inbox" },
  { to: "/queue", label: "Queue", icon: ListChecks, testId: "nav-queue" },
  { to: "/export", label: "Export", icon: Download, testId: "nav-export", admin: true },
  {
    to: "/extract",
    label: "Manual",
    title: "Manual extract — upload files or paste an email",
    icon: Upload,
    testId: "nav-extract",
    admin: true,
  },
];

// Reachable from the avatar menu on desktop; shown as a secondary row in the mobile menu.
const ADMIN_UTILS = [
  { to: "/activity", label: "Activity log", icon: ScrollText, testId: "nav-activity" },
  { to: "/settings", label: "Settings", icon: SettingsIcon, testId: "nav-settings" },
];

export const RoleBadge = ({ role, className }) => {
  const admin = role === "admin";
  const Icon = admin ? ShieldCheck : Eye;
  return (
    <span
      className={cn(
        "inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold",
        admin
          ? "border-success-border bg-success-surface text-success-text"
          : "border-border bg-secondary text-muted-foreground",
        className
      )}
      data-testid="user-role-badge"
    >
      <Icon className="h-2.5 w-2.5" aria-hidden="true" />
      {admin ? "Admin" : "Viewer"}
    </span>
  );
};

export const GmailPill = ({ status, syncing, onSync, canManage }) => {
  if (!status) return null;
  const connected = status.connected;
  const needsReconnect = status.needs_reconnect;
  const tone = connected
    ? "border-success-border bg-success-surface text-success-text"
    : needsReconnect
      ? "border-danger-border bg-danger-surface text-danger-text"
      : "border-warning-border bg-warning-surface text-warning-text";
  const label = connected
    ? status.account?.email || "Gmail connected"
    : needsReconnect
      ? "Gmail needs reconnect"
      : status.configured
        ? "Gmail not connected"
        : "Gmail not configured";
  return (
    <div className="flex items-center gap-1.5">
      {connected ? (
        <span
          className="hidden text-[11px] text-muted-foreground xl:inline"
          data-testid="header-last-sync"
        >
          synced {relTime(status.account?.last_sync_at) || "never"}
        </span>
      ) : null}
      <span
        className={cn(
          "inline-flex max-w-[132px] items-center gap-1.5 truncate rounded-full border px-2.5 py-1 text-[11px] font-medium sm:max-w-[200px] xl:max-w-[240px]",
          tone
        )}
        title={
          connected
            ? `Last sync: ${status.account?.last_sync_summary || "-"} (${
                relTime(status.account?.last_sync_at) || "never"
              })`
            : label
        }
        data-testid="gmail-status-pill"
      >
        {connected ? (
          <CheckCircle2 className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        ) : (
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
        )}
        <span className="truncate">{label}</span>
      </span>
      {canManage && connected ? (
        <Button
          variant="outline"
          size="sm"
          className="h-7 gap-1 bg-card px-2 text-[11px]"
          onClick={onSync}
          disabled={syncing}
          title="Fetch now"
          data-testid="sync-now-button"
        >
          {syncing ? (
            <Loader2 className="h-3 w-3 animate-spin" aria-hidden="true" />
          ) : (
            <RefreshCw className="h-3 w-3" aria-hidden="true" />
          )}
          {/* label only where the header has room: below xl the nav + pill + avatar
              already fill the row, so keep this icon-only to avoid overflow */}
          <span className="hidden xl:inline">Fetch now</span>
          <span className="sr-only xl:hidden">Fetch now</span>
        </Button>
      ) : null}
      {canManage && !connected ? (
        <Button asChild variant="outline" size="sm" className="h-7 bg-card px-2 text-[11px]">
          <Link to="/settings" data-testid="connect-gmail-link">
            {needsReconnect ? "Reconnect" : "Connect"}
          </Link>
        </Button>
      ) : null}
    </div>
  );
};

export default function AppShell({ children }) {
  const [status, setStatus] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const headerRef = useRef(null);
  const location = useLocation();
  const { user, isAdmin, logout } = useAuth();

  const nav = NAV.filter((item) => !item.admin || isAdmin);

  // Publish the live header height so viewport-locked layouts (Inbox two-pane)
  // can size themselves without hardcoding a magic pixel value.
  useEffect(() => {
    const el = headerRef.current;
    if (!el) return undefined;
    const write = () =>
      document.documentElement.style.setProperty("--app-header-h", `${el.offsetHeight}px`);
    write();
    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", write);
      return () => window.removeEventListener("resize", write);
    }
    const ro = new ResizeObserver(write);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const refresh = useCallback(async () => {
    try {
      setStatus(await gmailStatus());
    } catch (e) {
      /* non critical */
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [refresh, location.pathname]);

  useEffect(() => {
    setMobileNav(false);
  }, [location.pathname]);

  const onSync = async () => {
    setSyncing(true);
    try {
      const res = await gmailSync(false);
      if (res?.skipped) toast("A sync is already running");
      else
        toast.success("Gmail sync complete", {
          description: res?.summary || "Inbox is up to date.",
        });
      window.dispatchEvent(new CustomEvent("gmail-synced"));
      refresh();
    } catch (e) {
      toast.error("Sync failed", { description: errMessage(e) });
    } finally {
      setSyncing(false);
    }
  };

  const signOut = async () => {
    await logout();
    toast.success("Signed out");
  };

  // The inbox drives its own viewport-height two-pane layout, so the page wrapper
  // must not add vertical padding that would push the panes past the fold.
  const isInbox = location.pathname === "/";

  return (
    <div className="min-h-screen bg-background">
      <header
        ref={headerRef}
        className="vde-header-accent sticky top-0 z-40 border-b border-border"
        data-testid="app-header"
      >
        {/* decorative goSTOPS stripe */}
        <BrandStripe />

        <div className="mx-auto flex max-w-[1680px] items-center gap-3 px-4 py-2.5 sm:px-6 lg:px-8">
          <Link
            to="/"
            className="flex shrink-0 items-center gap-2.5 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            data-testid="app-logo"
          >
            <BrandIcon className="sm:hidden" />
            <BrandWordmark className="hidden sm:block" />
            <span className="hidden border-l border-border pl-2.5 sm:block">
              <span className="block text-sm font-bold leading-tight tracking-tight">
                Vendor Extractor
              </span>
              <span className="block text-[10px] font-medium text-muted-foreground">
                Payments inbox · Claude vision
              </span>
            </span>
          </Link>

          <nav className="ml-2 hidden min-w-0 flex-1 items-center gap-1 lg:flex" aria-label="Main">
            {nav.map(({ to, label, title, icon: Icon, testId }) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                title={title || label}
                data-testid={testId}
                className={({ isActive }) =>
                  cn(
                    "inline-flex items-center gap-1.5 whitespace-nowrap rounded-lg px-2.5 py-2 text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "text-foreground hover:bg-secondary"
                  )
                }
              >
                <Icon className="h-4 w-4" aria-hidden="true" />
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="ml-auto flex shrink-0 items-center gap-2">
            <GmailPill status={status} syncing={syncing} onSync={onSync} canManage={isAdmin} />

            <ThemeToggle />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 rounded-full border border-border bg-card py-0.5 pl-0.5 pr-2 text-left transition-colors hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1"
                  aria-label="Account menu"
                  data-testid="user-menu-button"
                >
                  <Avatar className="h-7 w-7">
                    <AvatarImage src={user?.picture || ""} alt="" />
                    <AvatarFallback className="bg-secondary text-[10px] font-semibold text-primary">
                      {initials(user?.name || user?.email || "?")}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden max-w-[120px] truncate text-[12px] font-medium xl:block">
                    {user?.name || user?.email}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-64 bg-popover/95 backdrop-blur-md"
                data-testid="user-menu"
              >
                <DropdownMenuLabel className="pb-1">
                  <p className="truncate text-[13px] font-semibold" data-testid="user-menu-name">
                    {user?.name || "Signed in"}
                  </p>
                  <p
                    className="vde-code truncate text-[11px] font-normal text-muted-foreground"
                    data-testid="user-menu-email"
                  >
                    {user?.email}
                  </p>
                  <RoleBadge role={user?.role} className="mt-1.5" />
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {isAdmin ? (
                  <>
                    <DropdownMenuItem asChild className="text-[13px]">
                      <Link to="/activity" data-testid="user-menu-activity">
                        <ScrollText className="mr-2 h-3.5 w-3.5" aria-hidden="true" /> Activity log
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuItem asChild className="text-[13px]">
                      <Link to="/settings" data-testid="user-menu-settings">
                        <SettingsIcon className="mr-2 h-3.5 w-3.5" aria-hidden="true" /> Settings
                      </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                ) : (
                  <>
                    <DropdownMenuLabel className="text-[11px] font-normal leading-relaxed text-muted-foreground">
                      Read-only access. Ask an admin for edit rights.
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={signOut}
                  className="text-[13px] text-danger-text focus:bg-danger-surface focus:text-danger-text"
                  data-testid="sign-out-button"
                >
                  <LogOut className="mr-2 h-3.5 w-3.5" aria-hidden="true" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button
              variant="outline"
              size="icon"
              className="h-8 w-8 bg-card lg:hidden"
              onClick={() => setMobileNav((v) => !v)}
              aria-label="Toggle navigation"
              data-testid="mobile-nav-toggle"
            >
              {mobileNav ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
            </Button>
          </div>
        </div>

        {mobileNav ? (
          <nav
            className="border-t border-border bg-card px-3 py-2 lg:hidden"
            aria-label="Mobile"
            data-testid="mobile-nav"
          >
            <div className="grid grid-cols-2 gap-1.5">
              {nav.map(({ to, label, title, icon: Icon, testId }) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  title={title || label}
                  data-testid={`m-${testId}`}
                  className={({ isActive }) =>
                    cn(
                      "inline-flex min-h-[44px] items-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-medium",
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-foreground"
                    )
                  }
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {label}
                </NavLink>
              ))}
            </div>
            {/* Admin utilities: kept out of the top bar, but reachable on small screens. */}
            {isAdmin ? (
              <div className="mt-1.5 grid grid-cols-2 gap-1.5 border-t border-border pt-1.5">
                {ADMIN_UTILS.map(({ to, label, icon: Icon, testId }) => (
                  <NavLink
                    key={to}
                    to={to}
                    data-testid={`m-${testId}`}
                    className={({ isActive }) =>
                      cn(
                        "inline-flex min-h-[44px] items-center gap-2 rounded-lg px-3 py-2.5 text-[13px] font-medium",
                        isActive
                          ? "bg-primary text-primary-foreground"
                          : "text-muted-foreground hover:bg-secondary"
                      )
                    }
                  >
                    <Icon className="h-4 w-4" aria-hidden="true" />
                    {label}
                  </NavLink>
                ))}
              </div>
            ) : null}
          </nav>
        ) : null}
      </header>

      <main
        className={cn(
          "mx-auto w-full max-w-[1680px] px-4 sm:px-6 lg:px-8",
          isInbox
            ? "py-4 sm:py-5 xl:flex xl:h-[calc(100dvh-var(--app-header-h,62px))] xl:flex-col xl:overflow-hidden"
            : "py-5 sm:py-6"
        )}
      >
        {status?.needs_reconnect ? (
          <div
            className="mb-5 flex shrink-0 flex-col gap-3 rounded-xl border border-danger-border bg-danger-surface p-4 sm:flex-row sm:items-center sm:justify-between"
            role="alert"
            data-testid="reconnect-banner"
          >
            <div className="flex items-start gap-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-danger-text" aria-hidden="true" />
              <div>
                <p className="text-[13px] font-semibold text-danger-text">
                  Gmail access needs to be reconnected
                </p>
                <p className="mt-0.5 text-xs text-danger-text">
                  {status.account?.last_sync_error ||
                    "The stored Google refresh token was rejected, so new payment mails are not being fetched."}
                  {isAdmin ? "" : " Ask an admin to reconnect the mailbox."}
                </p>
              </div>
            </div>
            {isAdmin ? (
              <Button asChild size="sm" variant="destructive" className="h-8 shrink-0 text-xs">
                <Link to="/settings" data-testid="reconnect-banner-button">
                  Reconnect Gmail
                </Link>
              </Button>
            ) : null}
          </div>
        ) : null}
        {status?.mailbox_mismatch ? (
          <div
            className="mb-5 flex shrink-0 items-start gap-3 rounded-xl border border-warning-border bg-warning-surface p-4"
            role="alert"
            data-testid="mailbox-mismatch-banner"
          >
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-warning-text" aria-hidden="true" />
            <p className="text-xs text-warning-text">
              Connected as <span className="font-semibold">{status.account?.email}</span>, but this tool expects the
              service mailbox <span className="font-semibold">{status.expected_mailbox}</span>. Group mail may not
              appear.{" "}
              {isAdmin ? (
                <Link
                  to="/settings"
                  className="font-semibold underline underline-offset-2"
                  data-testid="mailbox-mismatch-link"
                >
                  Change the connected account
                </Link>
              ) : (
                <span className="font-semibold">Ask an admin to switch the connected account.</span>
              )}
            </p>
          </div>
        ) : null}
        {children}
      </main>
    </div>
  );
}
